import { type PartialDeep } from 'type-fest';

import { type PartialPropsOf } from '@blaze-runtime/engine-domain';
import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';
import {
  ITEMS_CONTAINER_ACTION_DELETE,
  ITEMS_CONTAINER_ACTION_EDIT,
  ITEMS_CONTAINER_ACTION_VIEW,
  ITEMS_CONTAINER_BULK_ACTION_DELETE,
  ITEMS_CONTAINER_BULK_ACTION_EDIT,
  ITEMS_CONTAINER_BULK_ACTION_EXPORT,
  type PredefinedRowAction,
  type PropertiesConfigInputContainerItemUnifiedBulkActionModel,
  type PropertiesConfigInputContainerItemUnifiedRowActionModel,
} from '@blaze-shared/elements-domain';
import { type FontStyleOption } from '@blaze-shared/theming-domain';

/**
 * Migrates font style knob bold option (buttons in array) to a font weight select.
 * @param font
 * @param boldWeight
 * @param setWeightIfMissing - If `bold` is missing in style options set font weight from this parameter.
 */
export function migrateFontBoldStyleToFontWeightMutably(
  font:
    | { style?: FontStyleOption[]; fontWeight?: FontWeightOption; [key: string]: any }
    | undefined,
  boldWeight: FontWeightOption = '700',
  setWeightIfMissing?: FontWeightOption,
): void {
  if (!font?.style) {
    return;
  }

  if (font.style.includes('bold')) {
    font.fontWeight = boldWeight;
    font.style = font.style.filter((style) => style !== 'bold');
    return;
  }

  if (setWeightIfMissing) {
    font.fontWeight = setWeightIfMissing;
  }
}

export function migrateUnifiedRowActionsMutably(
  props: PartialPropsOf<'table' | 'list'> | undefined,
): void {
  if (props?.content?.unifiedRowActions !== undefined) {
    return;
  }

  const unifiedActions: PropertiesConfigInputContainerItemUnifiedRowActionModel[] = [];
  const { itemView, itemEdit, itemDelete, rowActions, actions } = props?.content ?? {};
  const actionsItems =
    actions && 'items' in actions && Array.isArray(actions.items) ? actions.items : [];
  const allActions = [...(rowActions ?? []), ...actionsItems];

  for (const action of allActions) {
    unifiedActions.push({
      type: 'custom' as const,
      ...action,
    });
  }

  if (itemView?.enabled) {
    const itemViewUnified: PropertiesConfigInputContainerItemUnifiedRowActionModel = {
      type: 'view' as const,
      label: 'View',
      id: ITEMS_CONTAINER_ACTION_VIEW,
      icon: 'icon_preview_16',
      showInlineLabel: false,
      styleSheetId: itemView?.styleSheetId,
    };
    migrateStyleForActionMutably(itemView, itemViewUnified);
    unifiedActions.push(itemViewUnified);
  }

  if (itemEdit?.enabled) {
    const itemEditUnified: PropertiesConfigInputContainerItemUnifiedRowActionModel = {
      type: 'edit' as const,
      label: 'Edit',
      id: ITEMS_CONTAINER_ACTION_EDIT,
      icon: 'icon_edit_24',
      showInlineLabel: false,
    };
    migrateStyleForActionMutably(itemEdit, itemEditUnified);
    unifiedActions.push(itemEditUnified);
  }

  if (itemDelete?.enabled) {
    const itemDeleteUnfied: PropertiesConfigInputContainerItemUnifiedRowActionModel = {
      type: 'delete' as const,
      label: 'Delete',
      id: ITEMS_CONTAINER_ACTION_DELETE,
      icon: 'icon_trash_16',
      showInlineLabel: false,
    };
    migrateStyleForActionMutably(itemDelete, itemDeleteUnfied);
    unifiedActions.push(itemDeleteUnfied);
  }

  if (props?.content) {
    props.content.unifiedRowActions = unifiedActions;
  }
}

export function migrateUnifiedBulkActionsMutably(props: PartialPropsOf<'table'> | undefined): void {
  if (props?.content?.unifiedBulkActions !== undefined) {
    return;
  }

  const unifiedActions: PropertiesConfigInputContainerItemUnifiedBulkActionModel[] = [];
  const { bulkExport, bulkEdit, bulkDelete, bulkActions } = props?.content ?? {};
  for (const action of bulkActions || []) {
    unifiedActions.push({
      type: 'custom' as const,
      ...action,
    });
  }

  if (bulkExport?.enabled) {
    const bulkExportUnified: PropertiesConfigInputContainerItemUnifiedBulkActionModel = {
      type: 'export' as const,
      label: 'Export',
      id: ITEMS_CONTAINER_BULK_ACTION_EXPORT,
      icon: 'icon_export_16',
      showInlineLabel: false,
    };
    migrateStyleForActionMutably(bulkExport, bulkExportUnified);
    unifiedActions.push(bulkExportUnified);
  }

  if (bulkDelete?.enabled) {
    const bulkDeleteUnified: PropertiesConfigInputContainerItemUnifiedBulkActionModel = {
      type: 'delete' as const,
      label: 'Delete',
      id: ITEMS_CONTAINER_BULK_ACTION_DELETE,
      icon: 'icon_trash_16',
      showInlineLabel: false,
    };
    migrateStyleForActionMutably(bulkDelete, bulkDeleteUnified);
    unifiedActions.push(bulkDeleteUnified);
  }

  if (bulkEdit?.enabled) {
    const bulkEditUnified: PropertiesConfigInputContainerItemUnifiedBulkActionModel = {
      type: 'edit' as const,
      label: 'Bulk Edit',
      id: ITEMS_CONTAINER_BULK_ACTION_EDIT,
      icon: 'icon_edit_24',
      showInlineLabel: false,
    };
    migrateStyleForActionMutably(bulkEdit, bulkEditUnified);
    unifiedActions.push(bulkEditUnified);
  }

  if (props) {
    props.content = {
      ...props.content,
      unifiedBulkActions: unifiedActions,
    };
  }
}

export function migrateStyleForActionMutably(
  legacyRowAction: PartialDeep<PredefinedRowAction, { allowUndefinedInNonTupleArrays: false }>,
  unifiedAction:
    | PropertiesConfigInputContainerItemUnifiedRowActionModel
    | PropertiesConfigInputContainerItemUnifiedBulkActionModel,
): void {
  const { styleSheetId, styleSheetTarget } = legacyRowAction;

  if (styleSheetTarget) {
    unifiedAction.styleSheetTarget = styleSheetTarget;
  } else if (styleSheetId) {
    unifiedAction.styleSheetId = styleSheetId;
  }
}
